import { HttpRequest, HttpEvent, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { returnUrlParams } from '../utils/return-url';

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);

/** True when the request targets the baked-in app API for this build (dev/uat/prod/local). */
export function isAppApiRequest(url: string): boolean {
  return url.startsWith(environment.apiURL);
}

export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const router = inject(Router);
  const http = inject(HttpClient);

  // Only send cookies / handle 401 refresh for our own API. Third-party
  // calls (e.g. GitHub Search) reject credentialed CORS requests.
  if (!isAppApiRequest(req.url)) {
    return next(req);
  }

  const clonedRequest = req.clone({
    withCredentials: true,
  });

  return next(clonedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only handle 401 errors and skip auth-related endpoints to avoid loops
      if (error.status === 401 && !isAuthEndpoint(req.url)) {
        return handle401Error(req, next, router, http);
      }
      return throwError(() => error);
    })
  );
}

function isAuthEndpoint(url: string): boolean {
  const authEndpoints = ['token/refresh', 'token/verify', 'logout', 'auth/login'];
  return authEndpoints.some((endpoint) => url.includes(endpoint));
}

function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  router: Router,
  http: HttpClient
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshUrl = `${environment.apiURL}authentication/token/refresh/`;

    return http.post(refreshUrl, {}, { withCredentials: true }).pipe(
      switchMap(() => {
        isRefreshing = false;
        refreshTokenSubject.next(true);
        // Retry the original request
        return next(request.clone({ withCredentials: true }));
      }),
      catchError((refreshError) => {
        isRefreshing = false;
        refreshTokenSubject.next(false);
        // Refresh failed - redirect to login, remembering the current page
        router.navigate(['/auth/login'], { queryParams: returnUrlParams(router.url) });
        return throwError(() => refreshError);
      })
    );
  } else {
    // Wait for the refresh to complete
    return refreshTokenSubject.pipe(
      filter((result) => result !== null),
      take(1),
      switchMap((success) => {
        if (success) {
          // Retry the original request
          return next(request.clone({ withCredentials: true }));
        }
        return throwError(() => new Error('Token refresh failed'));
      })
    );
  }
}

